import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { fetchProducts, ShopifyProduct } from "@/lib/shopify";
import { useLanguageStore } from "@/stores/languageStore";

interface SimpleSearchProps {
  onClose?: () => void;
}

export const SimpleSearch = ({ onClose }: SimpleSearchProps) => {
  const navigate = useNavigate();
  const { t } = useLanguageStore();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ShopifyProduct[]>([]);
  const [allProducts, setAllProducts] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch products on mount
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const response = await fetchProducts(100);
        setAllProducts(response.products);
      } catch (error) {
        console.error("Failed to fetch products:", error);
      }
    };
    loadProducts();
  }, []);

  // Filter products based on query
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    const searchTerm = query.toLowerCase();
    const filtered = allProducts.filter((product) =>
      product.node.title.toLowerCase().includes(searchTerm) ||
      product.node.description?.toLowerCase().includes(searchTerm)
    );
    setResults(filtered.slice(0, 5));
    setLoading(false);
    setShowResults(true);
  }, [query, allProducts]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (product: ShopifyProduct) => {
    navigate(`/product/${product.node.handle}`);
    setQuery("");
    setShowResults(false);
    onClose?.();
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('search.placeholder')}
          className="pr-10 pl-10 w-64"
          onFocus={() => query && setShowResults(true)}
        />
        {query && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-1 top-1/2 -translate-y-1/2 h-6 w-6"
            onClick={() => {
              setQuery("");
              setResults([]);
            }}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      {/* Results Dropdown */}
      {showResults && query && (
        <div className="absolute top-full mt-2 w-full bg-popover border border-border rounded-lg shadow-lg z-50 overflow-hidden">
          {loading ? (
            <div className="p-4 flex items-center justify-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : results.length > 0 ? (
            <div className="max-h-80 overflow-y-auto">
              {results.map((product) => (
                <button
                  key={product.node.id}
                  onClick={() => handleSelect(product)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-secondary/50 transition-colors text-right"
                >
                  <div className="w-12 h-12 rounded-md overflow-hidden bg-secondary flex-shrink-0">
                    {product.node.images?.edges?.[0]?.node && (
                      <img
                        src={product.node.images.edges[0].node.url}
                        alt={product.node.title}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate text-sm">
                      {product.node.title}
                    </p>
                    <p className="text-sm text-primary font-semibold">
                      ₪{parseFloat(product.node.priceRange.minVariantPrice.amount).toFixed(0)}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="p-4 text-center text-muted-foreground text-sm">
              {t('search.noResults')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
